import { useState, useEffect, useCallback, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { Mail, MessageCircle, FileText, Loader2, CheckCircle, AlertTriangle } from "lucide-react";

interface NotifConfig {
  readonly email: {
    readonly orderConfirmation: boolean;
    readonly shippingUpdate: boolean;
    readonly refundNotification: boolean;
    readonly senderName: string;
    readonly senderEmail: string;
  };
  readonly whatsapp: {
    readonly newOrderAlert: boolean;
    readonly statusUpdates: boolean;
    readonly reviewRequests: boolean;
    readonly bridgeConnected: boolean;
  };
  readonly shaam: {
    readonly webhookUrl: string;
    readonly apiKey: string;
    readonly connected: boolean;
  };
}

const DEFAULT_CONFIG: NotifConfig = {
  email: {
    orderConfirmation: true,
    shippingUpdate: true,
    refundNotification: true,
    senderName: "",
    senderEmail: "",
  },
  whatsapp: {
    newOrderAlert: true,
    statusUpdates: true,
    reviewRequests: true,
    bridgeConnected: false,
  },
  shaam: {
    webhookUrl: "",
    apiKey: "",
    connected: false,
  },
};

export default function NotificationSettings(): ReactNode {
  const { accessToken } = useAuth();
  const [config, setConfig] = useState<NotifConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingShaam, setTestingShaam] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const apiHeaders = useCallback(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    }),
    [accessToken],
  );

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch("/api/tenant/settings/notifications", { headers: apiHeaders() });
      const json = (await res.json()) as { success: boolean; data?: NotifConfig };
      if (json.success && json.data) {
        setConfig(json.data);
      }
    } catch {
      // Use defaults
    } finally {
      setLoading(false);
    }
  }, [apiHeaders]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch("/api/tenant/settings/notifications", {
        method: "PUT",
        headers: apiHeaders(),
        body: JSON.stringify(config),
      });
      const json = (await res.json()) as { success: boolean; error?: string };
      if (json.success) {
        setMessage({ type: "success", text: "\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA \u05E0\u05E9\u05DE\u05E8\u05D5 \u05D1\u05D4\u05E6\u05DC\u05D7\u05D4" }); // "Settings saved"
      } else {
        setMessage({ type: "error", text: json.error ?? "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05DE\u05D9\u05E8\u05D4" });
      }
    } catch {
      setMessage({ type: "error", text: "\u05E9\u05D2\u05D9\u05D0\u05EA \u05E8\u05E9\u05EA" });
    } finally {
      setSaving(false);
    }
  };

  const handleTestShaam = async () => {
    setTestingShaam(true);
    setMessage(null);
    try {
      const res = await fetch("/api/shaam/test", {
        method: "POST",
        headers: apiHeaders(),
      });
      const json = (await res.json()) as { success: boolean; data?: { connected: boolean }; error?: string };
      if (json.success && json.data?.connected) {
        setConfig((prev) => ({
          ...prev,
          shaam: { ...prev.shaam, connected: true },
        }));
        setMessage({ type: "success", text: "\u05D7\u05D9\u05D1\u05D5\u05E8 \u05DC\u05DE\u05E2\u05E8\u05DB\u05EA \u05D7\u05E9\u05D1\u05D5\u05E0\u05D9\u05D5\u05EA \u05EA\u05E7\u05D9\u05DF" }); // "Connection OK"
      } else {
        setMessage({ type: "error", text: json.error ?? "\u05D7\u05D9\u05D1\u05D5\u05E8 \u05E0\u05DB\u05E9\u05DC" }); // "Connection failed"
      }
    } catch {
      setMessage({ type: "error", text: "\u05E9\u05D2\u05D9\u05D0\u05EA \u05E8\u05E9\u05EA" });
    } finally {
      setTestingShaam(false);
    }
  };

  function Toggle({ checked, onChange, label }: { readonly checked: boolean; readonly onChange: (v: boolean) => void; readonly label: string }) {
    return (
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className="flex items-center justify-between w-full py-3 gap-4 min-h-[44px]"
      >
        <span className="text-sm">{label}</span>
        <div
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-primary" : "bg-muted"}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${checked ? "ltr:translate-x-6 rtl:-translate-x-6" : "ltr:translate-x-1 rtl:-translate-x-1"}`}
          />
        </div>
      </button>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
          <AlertTriangle className="h-4 w-4 text-primary" />
        </div>
        <h1 className="text-lg font-bold">{"\u05D4\u05EA\u05E8\u05D0\u05D5\u05EA"}</h1>
      </div>

      {message && (
        <div className={`flex items-center gap-2.5 p-3.5 rounded-xl text-sm border ${
          message.type === "success"
            ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
            : "bg-red-500/10 text-red-400 border-red-500/20"
        }`}>
          {message.type === "success"
            ? <CheckCircle className="h-4 w-4 shrink-0" />
            : <AlertTriangle className="h-4 w-4 shrink-0" />}
          {message.text}
        </div>
      )}

      {/* Email Notifications */}
      <Card className="border-border/60 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2.5 text-base">
            <div className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center">
              <Mail className="h-3.5 w-3.5 text-blue-400" />
            </div>
            {"\u05D4\u05EA\u05E8\u05D0\u05D5\u05EA \u05D0\u05D9\u05DE\u05D9\u05D9\u05DC"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-0 divide-y divide-border/40">
          <Toggle
            checked={config.email.orderConfirmation}
            onChange={(v) => setConfig((p) => ({ ...p, email: { ...p.email, orderConfirmation: v } }))}
            label={"\u05D0\u05D9\u05E9\u05D5\u05E8 \u05D4\u05D6\u05DE\u05E0\u05D4"}
          />
          <Toggle
            checked={config.email.shippingUpdate}
            onChange={(v) => setConfig((p) => ({ ...p, email: { ...p.email, shippingUpdate: v } }))}
            label={"\u05E2\u05D3\u05DB\u05D5\u05DF \u05DE\u05E9\u05DC\u05D5\u05D7"}
          />
          <Toggle
            checked={config.email.refundNotification}
            onChange={(v) => setConfig((p) => ({ ...p, email: { ...p.email, refundNotification: v } }))}
            label={"\u05D4\u05D5\u05D3\u05E2\u05EA \u05D4\u05D7\u05D6\u05E8"}
          />
          <div className="pt-4 pb-1 space-y-3">
            <div>
              <label className="text-sm font-medium text-muted-foreground">{"\u05E9\u05DD \u05E9\u05D5\u05DC\u05D7"}</label>
              <Input
                value={config.email.senderName}
                onChange={(e) => setConfig((p) => ({ ...p, email: { ...p.email, senderName: e.target.value } }))}
                placeholder={"\u05E9\u05DD \u05D4\u05E2\u05E1\u05E7"}
                className="mt-1.5 min-h-[44px]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">{"\u05D0\u05D9\u05DE\u05D9\u05D9\u05DC \u05E9\u05D5\u05DC\u05D7"}</label>
              <Input
                value={config.email.senderEmail}
                onChange={(e) => setConfig((p) => ({ ...p, email: { ...p.email, senderEmail: e.target.value } }))}
                placeholder="noreply@example.com"
                dir="ltr"
                className="mt-1.5 min-h-[44px]"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp Notifications */}
      <Card className="border-border/60 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2.5 text-base">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <MessageCircle className="h-3.5 w-3.5 text-emerald-400" />
            </div>
            {"\u05D4\u05EA\u05E8\u05D0\u05D5\u05EA \u05D5\u05D5\u05D0\u05D8\u05E1\u05D0\u05E4"}
            {config.whatsapp.bridgeConnected ? (
              <Badge className="text-xs bg-emerald-500/15 text-emerald-400 border-emerald-500/30 border ms-1">{"\u05DE\u05D7\u05D5\u05D1\u05E8"}</Badge>
            ) : (
              <Badge className="text-xs bg-amber-500/15 text-amber-400 border-amber-500/30 border ms-1">{"\u05DC\u05D0 \u05DE\u05D7\u05D5\u05D1\u05E8"}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-0 divide-y divide-border/40">
          <Toggle
            checked={config.whatsapp.newOrderAlert}
            onChange={(v) => setConfig((p) => ({ ...p, whatsapp: { ...p.whatsapp, newOrderAlert: v } }))}
            label={"\u05D4\u05EA\u05E8\u05D0\u05D4 \u05E2\u05DC \u05D4\u05D6\u05DE\u05E0\u05D4 \u05D7\u05D3\u05E9\u05D4 (\u05DC\u05D1\u05E2\u05DC\u05D9\u05DD)"}
          />
          <Toggle
            checked={config.whatsapp.statusUpdates}
            onChange={(v) => setConfig((p) => ({ ...p, whatsapp: { ...p.whatsapp, statusUpdates: v } }))}
            label={"\u05E2\u05D3\u05DB\u05D5\u05E0\u05D9 \u05E1\u05D8\u05D8\u05D5\u05E1 (\u05DC\u05DC\u05E7\u05D5\u05D7)"}
          />
          <Toggle
            checked={config.whatsapp.reviewRequests}
            onChange={(v) => setConfig((p) => ({ ...p, whatsapp: { ...p.whatsapp, reviewRequests: v } }))}
            label={"\u05D1\u05E7\u05E9\u05EA \u05D1\u05D9\u05E7\u05D5\u05E8\u05EA (\u05DC\u05DC\u05E7\u05D5\u05D7)"}
          />
        </CardContent>
      </Card>

      {/* SHAAM / Invoicing */}
      <Card className="border-border/60 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2.5 text-base">
            <div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <FileText className="h-3.5 w-3.5 text-amber-400" />
            </div>
            {"\u05D7\u05E9\u05D1\u05D5\u05E0\u05D9\u05D5\u05EA / SHAAM"}
            {config.shaam.connected && (
              <Badge className="text-xs bg-emerald-500/15 text-emerald-400 border-emerald-500/30 border ms-1">{"\u05DE\u05D7\u05D5\u05D1\u05E8"}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Warning banner */}
          <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{"\u05D7\u05D5\u05D1\u05EA \u05D7\u05E9\u05D1\u05D5\u05E0\u05D9\u05EA \u05D3\u05D9\u05D2\u05D9\u05D8\u05DC\u05D9\u05EA \u05DE\u05D9\u05D5\u05E0\u05D9 2026"}</span>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">{"Webhook URL (Green Invoice / iCount)"}</label>
            <Input
              value={config.shaam.webhookUrl}
              onChange={(e) => setConfig((p) => ({ ...p, shaam: { ...p.shaam, webhookUrl: e.target.value } }))}
              placeholder="https://api.greeninvoice.co.il/api/v1/..."
              dir="ltr"
              className="mt-1.5 min-h-[44px]"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">{"API Key"}</label>
            <Input
              type="password"
              value={config.shaam.apiKey}
              onChange={(e) => setConfig((p) => ({ ...p, shaam: { ...p.shaam, apiKey: e.target.value } }))}
              placeholder={"\u05DE\u05E4\u05EA\u05D7 API"}
              dir="ltr"
              className="mt-1.5 min-h-[44px]"
            />
          </div>
          <Button
            onClick={handleTestShaam}
            disabled={testingShaam || !config.shaam.webhookUrl}
            variant="outline"
            className="min-h-[44px] hover:bg-primary/10 hover:border-primary/30 transition-colors"
          >
            {testingShaam && <Loader2 className="h-4 w-4 animate-spin me-2" />}
            {"\u05D1\u05D3\u05D9\u05E7\u05EA \u05D7\u05D9\u05D1\u05D5\u05E8"}
          </Button>
        </CardContent>
      </Card>

      {/* Save button */}
      <div className="flex justify-end pb-2">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="min-h-[44px] min-w-[120px] shadow-sm"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin me-2" />}
          {"\u05E9\u05DE\u05D5\u05E8"}
        </Button>
      </div>
    </div>
  );
}
